import type { ComponentPropsWithRef, ElementType, ReactNode } from 'react';
import { cn } from '../lib/cn';

/**
 * Monospace is reserved for codes, times, amounts, IDs and labels in caps
 * (principle 9). Never use it for prose.
 */

/** 9px / 0.1em · 10px / 0.08em · 11px / 0.06em. */
export type MonoLabelSize = 9 | 10 | 11;

/**
 * `eyebrow` is the uppercase label above a title ("SIGNAAL", "NIET GESTELD").
 * `value` is inline data (`24-1183`, `€ 1.450`, `3 sep 09:00`) — not uppercased.
 */
export type MonoLabelVariant = 'eyebrow' | 'value';

export type MonoLabelTone =
    'default' | 'muted' | 'accent' | 'warn' | 'danger' | 'onDark' | 'onDarkMuted';

const toneStyles: Record<MonoLabelTone, string> = {
    default: 'text-[var(--text-secondary)]',
    muted: 'text-[var(--text-muted-mono-safe)]',
    accent: 'text-[var(--accent)]',
    warn: 'text-[var(--warn)]',
    danger: 'text-[var(--danger)]',
    onDark: 'text-[var(--text-on-dark)]',
    onDarkMuted: 'text-[var(--text-on-dark-4-safe)]',
};

const sizeStyles: Record<MonoLabelSize, string> = {
    9: 'text-[9px] tracking-[0.1em]',
    10: 'text-[10px] tracking-[0.08em]',
    11: 'text-[11px] tracking-[0.06em]',
};

export type MonoLabelProps = ComponentPropsWithRef<'span'> & {
    size?: MonoLabelSize;
    variant?: MonoLabelVariant;
    tone?: MonoLabelTone;
    as?: ElementType;
    children: ReactNode;
};

export function MonoLabel({
    size = 10,
    variant = 'eyebrow',
    tone = 'muted',
    as,
    className,
    children,
    ...rest
}: MonoLabelProps) {
    const Tag = (as ?? 'span') as ElementType;

    return (
        <Tag
            className={cn(
                'font-[family-name:var(--font-mono)] leading-[1.2]',
                variant === 'eyebrow' && 'uppercase',
                sizeStyles[size],
                toneStyles[tone],
                className,
            )}
            {...rest}
        >
            {children}
        </Tag>
    );
}
